/**
 * 🏁 Final Rounds — self-contained tab section: renders the tab and routes its
 * actions. Kept out of TAActionBoardView so the view stays small; tracking
 * state itself still lives in the view (shared with the other tabs), reached
 * through the callbacks below. The modal state and handlers live in
 * useFinalRoundActions — shared with the Requests tab's embedded final-round
 * cards — so the booking cascade has exactly one implementation.
 */
import { useEffect, useRef, useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import { fetchFinalHolds, holdFinalChains, releaseFinalHold } from '../../services/taFinalRoundApi';
import type {
  FinalRoundsSection as FinalRoundsData,
  RoleBoard,
  SlotHold,
  StageRef,
  TrackingEntry,
  TrackingKind,
  TrackingMap,
} from '../../types/taActionBoard';
import { FinalRoundsTab } from './FinalRoundsTab';
import type { FocusTarget } from './useFocusCard';
import type { PendingAction } from './usePendingActions';
import { useFinalRoundActions } from './useFinalRoundActions';

interface FinalRoundsSectionProps {
  roles: RoleBoard[];
  handled: Record<string, string>;
  /** Candidate the Overdue tab sent us to — passed straight through to the tab. */
  focus?: FocusTarget | null;
  tracking: TrackingMap;
  boardEmail: string;
  /** Reload the board — the shared pipeline strip's Lever retry. */
  onChanged?: () => void;
  /** The signed-in user — their Google grant reads the kick-off sheet. */
  myEmail: string;
  senderFirstName: string;
  /** Lever's stages in pipeline order — the post-debrief offer target. */
  stages: StageRef[];
  /** ACTIVE Lever archive reasons — the post-debrief rejection. Byte-exact. */
  archiveReasons: string[];
  /** The answering proxy predates the finalRounds section — the tab says so. */
  unavailable?: boolean;
  /** Optimistic tracking update + POST (the view's applyTracking). */
  onApplyTracking: (opportunityId: string, kind: TrackingKind, slots: string[], note?: string | null) => void;
  /** Local-only tracking update — for entries the SERVER already wrote (fr-booked). */
  onLocalTracking: (opportunityId: string, entry: TrackingEntry, status: string) => void;
  onMarkHandled: (opportunityId: string, label: string) => void;
  /** The board's Google token — the proposal email sends as the recruiter. */
  gmailToken: string | null;
  /** Queue the send behind the board's cancellable countdown. */
  onDefer: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /** Replace one role's finalRounds section (after template edits re-solve chains). */
  onFinalRoundsUpdate: (postingId: string, section: FinalRoundsData) => void;
}

export function FinalRoundsSection({
  roles,
  handled,
  focus = null,
  tracking,
  boardEmail,
  onChanged,
  myEmail,
  senderFirstName,
  stages,
  archiveReasons,
  unavailable,
  onApplyTracking,
  onLocalTracking,
  onMarkHandled,
  gmailToken,
  onDefer,
  onFinalRoundsUpdate,
}: FinalRoundsSectionProps) {
  // postingIds whose 🔄 re-check is in flight — a Set, because every open
  // accordion has its own live button and re-checks may overlap.
  const [rechecking, setRechecking] = useState<Set<string>>(new Set());

  // Final-round holds, per role — kept OUT of the cached board (they name the
  // candidate they were offered to, and change often), exactly like the case
  // slot holds. Fetched once for the roles that have a final round.
  const [finalHolds, setFinalHolds] = useState<Record<string, SlotHold[]>>({});
  // Roles the recruiter has already hold-mutated this session: the mount fetch
  // must not clobber them if it resolves AFTER a hold/release (a real race —
  // the fetch is stale by then).
  const mutated = useRef<Set<string>>(new Set());
  const holdPostingIds = roles
    .filter((r) => r.finalRounds.candidates.length > 0 && r.finalRounds.config)
    .map((r) => r.postingId);
  const holdIdsKey = holdPostingIds.join(',');
  useEffect(() => {
    if (!holdIdsKey) return;
    let live = true;
    fetchFinalHolds(holdIdsKey.split(','))
      .then((h) => {
        if (!live) return;
        setFinalHolds((prev) => {
          const merged = { ...h };
          for (const id of mutated.current) if (prev[id] !== undefined) merged[id] = prev[id];
          return merged;
        });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [holdIdsKey]);

  const onHoldChains = (postingId: string, starts: string[]) => {
    holdFinalChains({ postingId, starts, boardEmail })
      .then((r) => {
        mutated.current.add(postingId);
        setFinalHolds((prev) => ({ ...prev, [postingId]: r.holds }));
        showToast({
          status: r.created > 0 ? 'success' : 'warning',
          description:
            r.created > 0
              ? `Held ${r.created} final-round time${r.created === 1 ? '' : 's'}.`
              : 'Nothing new to hold — those times were already held.',
        });
      })
      .catch((e) =>
        showToast({ status: 'error', description: e instanceof Error ? e.message : 'Could not hold the times.' })
      );
  };

  const onReleaseFinalHold = (postingId: string, hold: SlotHold) => {
    releaseFinalHold({ postingId, starts: [hold.start] })
      .then((r) => {
        mutated.current.add(postingId);
        setFinalHolds((prev) => ({ ...prev, [postingId]: r.holds }));
      })
      .catch((e) =>
        showToast({ status: 'error', description: e instanceof Error ? e.message : 'Could not release the hold.' })
      );
  };

  const finalRound = useFinalRoundActions({
    boardEmail,
    senderFirstName,
    stages,
    archiveReasons,
    gmailToken,
    onApplyTracking,
    onLocalTracking,
    onMarkHandled,
    onDefer,
    onFinalRoundsUpdate,
  });

  /**
   * Re-solve ONE role's chains against fresh calendars, without touching the
   * template. Mirrors the case panel's 🔄: ALWAYS reports the outcome, because
   * an unchanged calendar re-renders an identical list and silence is
   * indistinguishable from a dead button.
   */
  const runRecheck = (role: RoleBoard) => {
    setRechecking((prev) => new Set(prev).add(role.postingId));
    api
      .fetchFinalRoundChains(role.postingId, boardEmail)
      .then(({ config, chains, moreChains, error, diagnosis, lookaheadDays }) => {
        onFinalRoundsUpdate(role.postingId, {
          candidates: role.finalRounds.candidates,
          config,
          chains,
          moreChains: moreChains ?? [],
          chainError: error,
          chainDiagnosis: diagnosis ?? null,
          ...(lookaheadDays ? { lookaheadDays } : {}),
        });
        const days = lookaheadDays ?? role.finalRounds.lookaheadDays ?? 10;
        if (error) {
          showToast({ status: 'warning', description: `Calendars could not be read: ${error}` });
          return;
        }
        showToast({
          status: chains.length > 0 ? 'success' : 'warning',
          description:
            chains.length > 0
              ? `Chains re-solved — ${chains.length} workable option${chains.length === 1 ? '' : 's'} in the next ${days} days.`
              : `Chains re-solved — still no workable chain in the next ${days} days.${
                  diagnosis ? ` ${diagnosis}` : ''
                }`,
        });
      })
      .catch((e) =>
        showToast({
          status: 'error',
          description: e instanceof Error ? e.message : 'Could not re-solve the chains.',
        })
      )
      .finally(() =>
        setRechecking((prev) => {
          const next = new Set(prev);
          next.delete(role.postingId);
          return next;
        })
      );
  };

  return (
    <>
      <FinalRoundsTab
        roles={roles}
        myEmail={myEmail}
        boardEmail={boardEmail}
        onChanged={onChanged}
        handled={handled}
        focus={focus}
        tracking={tracking}
        unavailable={unavailable}
        rechecking={rechecking}
        finalHolds={finalHolds}
        onHoldChains={onHoldChains}
        onReleaseFinalHold={onReleaseFinalHold}
        onAction={(role, action) => {
          if (action.type === 'fr-recheck') return runRecheck(role);
          return finalRound.onAction(role, action);
        }}
      />
      {finalRound.modals}
    </>
  );
}
